/**
 * Small shared helpers for the per-domain source files. They turn raw
 * dataset rows into `SeriesPoint[]` / `BreakdownSlice[]` without each
 * definition re-implementing the same group-by.
 */

import type { BreakdownSlice } from '../catalogTypes';
import type { SeriesPoint } from '../types';
import { monthKey } from '../series';

export function groupSum<T>(
  rows: readonly T[],
  keyOf: (row: T) => string | null | undefined,
  valueOf: (row: T) => number,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of rows) {
    const key = keyOf(row);
    if (!key) continue;
    out.set(key, (out.get(key) ?? 0) + valueOf(row));
  }
  return out;
}

export function groupCount<T>(rows: readonly T[], keyOf: (row: T) => string | null | undefined): Map<string, number> {
  return groupSum(rows, keyOf, () => 1);
}

/** Map → ascending series. */
export function toSeries(map: Map<string, number>): SeriesPoint[] {
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([t, v]) => ({ t, v }));
}

/** Map → slices sorted largest first, dropping empty categories. */
export function toSlices(map: Map<string, number>, opts?: { keepZero?: boolean }): BreakdownSlice[] {
  return Array.from(map.entries())
    .filter(([, v]) => opts?.keepZero || v !== 0)
    .sort(([, a], [, b]) => b - a)
    .map(([label, value]) => ({ label, value }));
}

export function dailySeries<T>(
  rows: readonly T[],
  dayOf: (row: T) => string | null | undefined,
  valueOf: (row: T) => number,
): SeriesPoint[] {
  return toSeries(groupSum(rows, (r) => dayOf(r)?.slice(0, 10), valueOf));
}

export function dailyCount<T>(rows: readonly T[], dayOf: (row: T) => string | null | undefined): SeriesPoint[] {
  return dailySeries(rows, dayOf, () => 1);
}

export function monthlySeries<T>(
  rows: readonly T[],
  dayOf: (row: T) => string | null | undefined,
  valueOf: (row: T) => number,
): SeriesPoint[] {
  return toSeries(
    groupSum(
      rows,
      (r) => {
        const d = dayOf(r);
        return d ? monthKey(d) : null;
      },
      valueOf,
    ),
  );
}

/**
 * Weighted daily average, e.g. cycle time weighted by units. Rows with a
 * null value or zero weight are skipped.
 */
export function dailyWeightedAvg<T>(
  rows: readonly T[],
  dayOf: (row: T) => string,
  valueOf: (row: T) => number | null,
  weightOf: (row: T) => number,
): SeriesPoint[] {
  const num = new Map<string, number>();
  const den = new Map<string, number>();
  for (const row of rows) {
    const v = valueOf(row);
    const w = weightOf(row);
    if (v == null || !Number.isFinite(v) || w <= 0) continue;
    const day = dayOf(row).slice(0, 10);
    num.set(day, (num.get(day) ?? 0) + v * w);
    den.set(day, (den.get(day) ?? 0) + w);
  }
  return Array.from(num.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([t, total]) => ({ t, v: total / (den.get(t) ?? 1) }));
}

/** Point-wise `a - b` over the union of both series' keys. */
export function subtractSeries(a: SeriesPoint[], b: SeriesPoint[]): SeriesPoint[] {
  const out = new Map<string, number>();
  for (const p of a) out.set(p.t, (out.get(p.t) ?? 0) + p.v);
  for (const p of b) out.set(p.t, (out.get(p.t) ?? 0) - p.v);
  return toSeries(out);
}

export function addSeries(a: SeriesPoint[], b: SeriesPoint[]): SeriesPoint[] {
  const out = new Map<string, number>();
  for (const p of a) out.set(p.t, (out.get(p.t) ?? 0) + p.v);
  for (const p of b) out.set(p.t, (out.get(p.t) ?? 0) + p.v);
  return toSeries(out);
}

export function inWindow(day: string | null | undefined, start: string, end: string): boolean {
  if (!day) return false;
  const d = day.slice(0, 10);
  return d >= start && d <= end;
}

/** Fractional days between two ISO timestamps / dates (b − a). */
export function daysBetween(a: string | null | undefined, b: string | null | undefined): number | null {
  if (!a || !b) return null;
  const ta = Date.parse(a.length === 10 ? `${a}T00:00:00Z` : a);
  const tb = Date.parse(b.length === 10 ? `${b}T00:00:00Z` : b);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null;
  return (tb - ta) / 86_400_000;
}

export function avg(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

export function sum(values: number[]): number {
  return values.reduce((s, v) => s + v, 0);
}

export function ratio(num: number, den: number): number | null {
  return den === 0 ? null : num / den;
}

export function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
